import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Card, CardContent, CircularProgress, Grid, Typography } from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Company, CompanyMember } from "../types";

interface DashboardStats {
  companyName: string;
  joinCode: string;
  members: number;
}

export default function Dashboard() {
  const authApi = useAuthApi();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const companies = (await authApi<Company[]>(`${MGMT}/companies`)) ?? [];
        if (companies.length === 0) {
          if (!cancelled) setStats(null);
          return;
        }
        if (companies.length > 1) {
          throw new Error("Your account is linked to multiple companies. Fleet portal supports one company per user.");
        }
        const company = companies[0];
        const members = await authApi<CompanyMember[]>(`${MGMT}/companies/${company.id}/memberships`);
        if (!cancelled) {
          setStats({
            companyName: company.name,
            joinCode: company.join_code,
            members: members?.length ?? 0,
          });
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authApi]);

  const cards = useMemo(
    () => (stats == null
      ? []
      : [
        { label: "Company", value: stats.companyName },
        { label: "Join Code", value: stats.joinCode },
        { label: "Active Members", value: String(stats.members) },
      ]),
    [stats]
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (stats == null) {
    return (
      <Alert severity="info">
        No company is assigned to your account yet. Ask a platform admin to grant fleet company access.
      </Alert>
    );
  }

  return (
    <Grid container spacing={2}>
      {cards.map((card) => (
        <Grid key={card.label} item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                {card.label}
              </Typography>
              <Typography variant="h4" sx={{ mt: 0.5 }}>
                {card.value}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}
